import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, 
  X, 
  AlertTriangle,
  Loader2,
  Printer
} from 'lucide-react';
import { SeenIcon } from '@/components/common/SeenLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorDisplay = () => {
  const { vendorId } = useParams();
  const [displayData, setDisplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDisplayData();
  }, [vendorId]);

  const fetchDisplayData = async () => {
    try {
      const response = await axios.get(`${API}/vendor/${vendorId}/display`);
      setDisplayData(response.data);
    } catch (error) {
      setError('Vendor not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#5DBDB3]" />
      </div>
    );
  }

  if (error || !displayData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-[#F08B7D]" />
          <h1 className="text-2xl font-bold">Vendor Not Found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-8 print:p-4" data-testid="vendor-display">
      {/* Print button - hidden when printing */}
      <div className="fixed top-4 right-4 print:hidden">
        <button 
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-[#5DBDB3] text-white rounded-lg hover:bg-[#4DA9A0]"
        >
          <Printer className="w-4 h-4" />
          Print Poster
        </button>
      </div>

      {/* Header */}
      <div className="text-center mb-8 print:mb-4">
        <div className="flex items-center justify-center gap-3 mb-4">
          <SeenIcon size={48} />
          <span className="text-3xl font-bold text-[#5DBDB3]">SEEN</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold mb-2 print:text-4xl">
          {displayData.vendor_name}
        </h1>
        <p className="text-xl text-gray-500">{displayData.festival_name}</p>
      </div>

      {/* Main content - Two columns */}
      <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
        {/* Safe Colors */}
        <div className="bg-[#5DBDB3]/10 rounded-3xl p-8 print:p-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-16 h-16 rounded-full bg-[#5DBDB3] flex items-center justify-center">
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#5DBDB3]">SAFE TO EAT</h2>
              <p className="text-gray-600">These wristband colors CAN eat here</p>
            </div>
          </div>
          
          <div className="grid grid-cols-4 md:grid-cols-5 gap-4 print:grid-cols-6 print:gap-2">
            {displayData.safe_colors.map((color) => (
              <div key={`safe-${color.allergen}-${color.color}`} className="text-center">
                <div
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full mx-auto border-4 border-white shadow-lg print:w-12 print:h-12"
                  style={{ backgroundColor: color.color }}
                />
                <p className="text-xs mt-2 font-medium text-gray-700 truncate">
                  {color.allergen}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Warning Colors */}
        <div className="bg-[#F08B7D]/10 rounded-3xl p-8 print:p-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-16 h-16 rounded-full bg-[#F08B7D] flex items-center justify-center">
              <X className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#F08B7D]">CANNOT EAT</h2>
              <p className="text-gray-600">These wristband colors should AVOID</p>
            </div>
          </div>
          
          {displayData.warning_colors.length > 0 ? (
            <div className="grid grid-cols-4 md:grid-cols-5 gap-4 print:grid-cols-6 print:gap-2">
              {displayData.warning_colors.map((color) => (
                <div key={`warn-${color.allergen}-${color.color}`} className="text-center">
                  <div
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full mx-auto border-4 border-[#F08B7D] shadow-lg relative print:w-12 print:h-12"
                    style={{ backgroundColor: color.color }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <X className="w-8 h-8 text-[#F08B7D] drop-shadow-lg" strokeWidth={3} />
                    </div>
                  </div>
                  <p className="text-xs mt-2 font-medium text-gray-700 truncate">
                    {color.allergen}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Check className="w-16 h-16 mx-auto text-[#5DBDB3] mb-4" />
              <p className="text-xl font-medium text-gray-600">
                No major allergens in our food!
              </p>
              <p className="text-gray-500">All wristband colors can eat safely</p>
            </div>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <div className="max-w-4xl mx-auto mt-8 print:mt-4">
        <h3 className="text-2xl font-bold text-center mb-4">Our Menu</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {displayData.food_items.map((item) => (
            <div key={`menu-${item.name}`} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <span className="text-lg font-medium">{item.name}</span>
              {item.contains_allergens?.length > 0 ? (
                <div className="flex gap-1 ml-auto">
                  {item.contains_allergens.map((allergen) => (
                    <span key={`${item.name}-${allergen}`} className="text-xs px-2 py-1 bg-[#F08B7D]/20 text-[#F08B7D] rounded-full">
                      {allergen}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-xs px-2 py-1 bg-[#5DBDB3]/20 text-[#5DBDB3] rounded-full ml-auto">
                  No major allergens
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-gray-400 text-sm print:mt-4">
        <p>Powered by SEEN - Beyond the label.</p>
        <p>If unsure, please ask our staff!</p>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: landscape; margin: 1cm; }
        }
      `}</style>
    </div>
  );
};

export default VendorDisplay;
